(function () {
	'use strict';

	angular.module('GorillaBoy.services')
	.factory('GetNextDocumentService', [
		'$log',
		'$q',
		'$translate',
		'AppConstants',
		'DocumentService',
		'FileService',
		'MessageManager',
		'SortService',
		'ContentTypeFormatter',
		GetNextDocumentService
	]);

	/*
	 * NOTE: We do not support the case where a document is restored or moved into the parent folder during a user's session.
	 * This is because the docIds list must be refreshed every time in order to support this.
	 * We DO support the following:
	 *  - Basic next/previous document navigation when coming from the list view
	 *  - Navigation support when arriving at a document through its link 
	 *    (unless more than five pagination calls are required to find its position inside of its parent folder)
	 *  - Updating our cache and notifying the user when the next/previous document in our list has been moved or deleted
	 *  - Caching of the sort options the user has chosen when coming from the list view
	 */
	function GetNextDocumentService ($log, $q, $translate, AppConstants, DocumentService, FileService, MessageManager, SortService, ContentTypeFormatter) {
		var nextDocService = {},

			docByFolder = {},
			markersByFolder = {},
			getDocIdsPromises = {},

			sortOptions = SortService.getSortOption(SortService.getType(), AppConstants.GalleryTypes.LIST_VIEWTYPE),
			getFolderContentsParams = {
				sort: sortOptions.sortBy,
				order: sortOptions.order,
				limit: AppConstants.MAX_DOCS,
				type: AppConstants.FolderContentTypes.DOCUMENT
			};

		/*
		 * Setter function for the docs list for a particular folder.
		 * @param {array} docIds
		 * @param {string} folderId
		 */
		nextDocService.setDocs = function (docs, folderId) {
			docByFolder[folderId] = docs;
		};

		/*
		 * Setter function for the docs list for a particular folder, exclusively.
		 * This means it will flush what is in docByFolder and then set docs for a folder
		 * @param {array} docIds
		 * @param {string} folderId
		 */
		nextDocService.setDocsExclusively = function (docs, folderId) {
			docByFolder = {};
			docByFolder[folderId] = docs;
		};

		/* Setter function for the marker for a particular folder.
		 * @param {string} marker
		 * @param {string} folderId
		 */
		nextDocService.setMarker = function (marker, folderId) {
			markersByFolder[folderId] = marker;
		};

		/* Getter function for the marker for a particular folder.
		 * @param {string} marker
		 * @param {string} folderId
		 */
		nextDocService.getMarker = function(folderId) {
			return markersByFolder[folderId]? markersByFolder[folderId]: null;
		};

		/* Get the image metadata of the previous image of given image.
		 * @param {string} currentImgId
		 * @param {string} parentFolderId
		 * @return {promise} the metadata of previous image of the given image
		 */
		nextDocService.getPreviousImg = function(currentImgId, parentFolderId) {
			var deferred = $q.defer();

			findCurrentImgPosition(currentImgId, parentFolderId).then(function(result) {
				var currentImgPosition = result.position,
					currentImgIndex = currentImgPosition - 1,
					parentFolderImgCount = result.count;
					if (currentImgPosition === 1) {
						deferred.reject('DOCUMENT_NAV.NO_PREVIOUS');
					} else {
						var allImages = docByFolder[parentFolderId].filter(isImageFile);
						var prevImg = allImages[currentImgIndex - 1];
						deferred.resolve(prevImg);
					}
			}, deferred.reject);

			return deferred.promise;
		};

		/* Get the image metadata of the next image of given image.
		 * @param {string} currentImgId
		 * @param {string} parentFolderId
		 * @return {promise} the metadata of next image of the given image
		 */
		nextDocService.getNextImg = function(currentImgId, parentFolderId) {
			var deferred = $q.defer();

			findCurrentImgPosition(currentImgId, parentFolderId).then(function(result) {
				var currentImgPosition = result.position,
					currentImgIndex = currentImgPosition - 1,
					parentFolderImgCount = result.count;
					if (currentImgPosition === parentFolderImgCount) {
						if (markersByFolder[parentFolderId]) {
							getNextPaginatedDocs(parentFolderId).then(function success () {
								// recursive call, but this time there should be more docIds.
								nextDocService.getNextImg(currentImgId, parentFolderId)
								.then(function success (doc) {
									deferred.resolve(doc);
								}, deferred.reject);
							}, deferred.reject);
						} else {
							deferred.reject('DOCUMENT_NAV.LAST_DOC');
						}
					} else {
						var allImages = docByFolder[parentFolderId].filter(isImageFile);
						var nextImg = allImages[currentImgIndex + 1];
						deferred.resolve(nextImg);
					}
			}, deferred.reject);

			return deferred.promise;
		};

		/*
		 * Retrieves the previous document in the parent folder.
		 * @param {string} currentDocId
		 * @param {string} parentFolderId
		 * @return {promise} promise containing the ID of the previous document.
		 */
		nextDocService.getPreviousDoc = function (currentDocId, parentFolderId) {
			var deferred = $q.defer(),
				prevDocId;

			findCurrentDocPosition(currentDocId, parentFolderId)
			.then(function success (position) {
				var docIds = docByFolder[parentFolderId].map(doc2docId);

				if (position > -1) {
					if (position === 0) {
						// We are at the first document, and we do not support looping to the last document.
						deferred.reject($translate.instant('DOCUMENT_NAV.NO_PREVIOUS'));
					} else {
						// Check that the previous document ID has not been moved or deleted.
						prevDocId = docIds[position - 1];
						validateDoc(prevDocId, parentFolderId)
						.then(function valid () {
							deferred.resolve(docByFolder[parentFolderId][position - 1]);
						}, function invalid () {
							MessageManager.addInfoMessage($translate.instant('DOCUMENT_NAV.MODIFIED_CONTENTS'));
							$log.info('Refreshing the document list and retrying the prev/next document request.');
							docByFolder[parentFolderId] = null;
							nextDocService.getPreviousDoc(currentDocId, parentFolderId)
							.then(function success (doc) {
								deferred.resolve(doc);
							}, function error (err) {
								deferred.reject(err);
							});
						});
					}
				} else {
					// We are unable to determine the position of the current document, so disable the feature.
					deferred.reject($translate.instant('DOCUMENT_NAV.TOO_MANY_DOCS'));
				}
			}, function error (err) {
				deferred.reject(err);
			});

			return deferred.promise;
		};

		/*
		 * Retrieves the next document in the parent folder.
		 * @param {string} currentDocId
		 * @param {string} parentFolderId
		 * @return {promise} promise containing the ID of the next document.
		 */
		nextDocService.getNextDoc = function (currentDocId, parentFolderId) {
			var deferred = $q.defer(),
				nextDocId;

			findCurrentDocPosition(currentDocId, parentFolderId)
			.then(function success (position) {
				var docIds = docByFolder[parentFolderId].map(doc2docId);

				if (position > -1) {
					if (position === docIds.length - 1) {
						// We are at the last document in our list
						if (markersByFolder[parentFolderId]) {
							// There are more documents as indicated by the presence of a marker.
							// We make a call to continue to populate the docIds list.
							getNextPaginatedDocs(parentFolderId).then(function success () {
								// recursive call, but this time there should be more docIds.
								nextDocService.getNextDoc(currentDocId, parentFolderId)
								.then(function success (doc) {
									deferred.resolve(doc);
								}, function error (err) {
									deferred.reject(err);
								});
							}, function error (err) {
								deferred.reject(err);
							});
						} else {
							// We do not support looping around to the first document.
							deferred.reject($translate.instant('DOCUMENT_NAV.LAST_DOC'));
						}
					} else {
						// Check that the next document ID has not been moved or deleted.
						nextDocId = docIds[position + 1];
						validateDoc(nextDocId, parentFolderId)
						.then(function valid () {
							deferred.resolve(docByFolder[parentFolderId][position + 1]);
						}, function invalid () {
							MessageManager.addInfoMessage($translate.instant('DOCUMENT_NAV.MODIFIED_CONTENTS'));
							$log.info('Refreshing the document list and retrying the prev/next document request.');
							docByFolder[parentFolderId] = null;
							nextDocService.getNextDoc(currentDocId, parentFolderId)
							.then(function success (doc) {
								deferred.resolve(doc);
							}, function error (err) {
								deferred.reject(err);
							});
						});
					}
				} else {
					// We are unable to determine the position of the current document, so disable the feature.
					deferred.reject($translate.instant('DOCUMENT_NAV.TOO_MANY_DOCS'));
				}
			}, function error (err) {
				deferred.reject(err);
			});

			return deferred.promise;
		};

		/*
		 * Determines whether to enable the previous document button for the current document.
		 * @param {string} currentDocId
		 * @param {string} parentFolderId
		 * @return {promise} promise containing a boolean for whether the button should be enabled
		 */
		nextDocService.enablePrevDocNavigation = function (currentDocId, parentFolderId) {
			var deferred = $q.defer();

			findCurrentDocPosition(currentDocId, parentFolderId)
			.then(function success (position) {
				deferred.resolve(position !== 0);
			}, function error () {
				deferred.resolve(false);
			});

			return deferred.promise;
		};

		/*
		 * Determines whether to enable the next document button for the current document.
		 * @param {string} currentDocId
		 * @param {string} parentFolderId
		 * @return {promise} promise containing a boolean for whether the button should be enabled
		 */
		nextDocService.enableNextDocNavigation = function (currentDocId, parentFolderId) {
			var deferred = $q.defer();

			if (markersByFolder[parentFolderId]) {
				deferred.resolve(true);
			} else {
				findCurrentDocPosition(currentDocId, parentFolderId)
				.then(function success (position) {
					deferred.resolve(position !== docByFolder[parentFolderId].length - 1);
				}, function error () {
					deferred.resolve(false);
				});
			}

			return deferred.promise;
		};

		nextDocService.findCurrentImgPosition = findCurrentImgPosition;

		/*
		 * Determines the position of an image in all images in a folder.
		 * @param {string} currentImgId
		 * @param {string} parentFolderId
		 * @return {promise} promise contains the position of the image
		 */
		function findCurrentImgPosition(currentImgId, parentFolderId) {
			var deferred = $q.defer();

			// Find the current doc position to populate doc list to docByFolder
			findCurrentDocPosition(currentImgId, parentFolderId).then(function success() {
				var allImages = docByFolder[parentFolderId].filter(isImageFile);
				var allImageIds = allImages.map(doc2docId);
				deferred.resolve({
					position: (allImageIds.indexOf(currentImgId) + 1),
					count: allImageIds.length});
			}, deferred.reject);

			return deferred.promise;
		}

		/*
		 * Finds and returns the position of the current document in its parent folder.
		 * @param {string} currentDocId
		 * @param {string} parentFolderId
		 * @return {promise} promise containing the position of the document. This will be -1 if the current document is not found in the parent folder.
		 */
		function findCurrentDocPosition (currentDocId, parentFolderId) {
			var deferred = $q.defer();

			getDocIds(parentFolderId).then(function success (docIds) {
				var position = findPositionInList(currentDocId, docIds);

				if (position > -1) {
					deferred.resolve(position);
				} else {
					// This means that our document is not in the first page of documents returned from the service.
					// Make pagination calls until the current document is found.
					paginateUntilCurrentDoc(currentDocId, parentFolderId).then(function success (position) {
						deferred.resolve(position);
					}, function error (err) {
						deferred.reject(err);
					});
				}
			}, function error (err) {
				deferred.reject(err);
			});

			return deferred.promise;
		}

		/*
		 * Retrieves the list of document IDs for a given folder ID.
		 * Caches previously retrieved lists of document IDs in the docByFolder object.
		 * Also caches the marker from the service's response in the markersByFolder object.
		 * @param {string} parentFolderId
		 * @return {promise} promise containing a list of docIds
		 */
		function getDocIds (parentFolderId) {
			var deferred = $q.defer();
			if (getDocIdsPromises[parentFolderId]) {
				return getDocIdsPromises[parentFolderId];
			}

			// If we have a list of docIds stored, use it instead of making a new getFolderContents call.
			if (docByFolder[parentFolderId]) {
				deferred.resolve(docByFolder[parentFolderId].map(doc2docId));
			} else {
				FileService.getFolderContents(parentFolderId, getFolderContentsParams)
				.then(function success (response) {
					var i,
						numDocs,
						docs = [];

					if (response.Documents) {
						docs = response.Documents;

						if (response.Marker) {
							markersByFolder[parentFolderId] = response.Marker;
						}

						docByFolder[parentFolderId] = docs;
						deferred.resolve(docs.map(doc2docId));
					} else {
						// This should never happen, but better safe than sorry.
						deferred.reject($translate.instant('DOCUMENT_NAV.EMPTY_PARENT'));
					}
				}, function error (err) {
					deferred.reject(err);
				});
			}
			deferred.promise.finally(function() {
				delete getDocIdsPromises[parentFolderId];
			});
			getDocIdsPromises[parentFolderId] = deferred.promise;
			return deferred.promise;
		}

		/*
		 * Helper function: Finds and returns the position of an item in a list
		 * @param {string} item
		 * @param {string} items
		 * @return {integer} position - This will be -1 if the list does not contain the given item.
		 */
		function findPositionInList (item, items) {
		 	var i = 0,
                numItems = items.length;

            for (i = 0; i < numItems; i++) {
                if (items[i] === item) {
                    return i;
                }
            }

            return -1;
		}

		/*
		 * Helper function: Makes pagination calls until either the current document is found,
		 * or the number of calls to the service reaches 5.
		 * @param {string} currentDocId
		 * @param {string} parentFolderId
		 * @return {promise} promise containing the position of the document. This will be -1 if the list does not contain the given item.
		 */
		function paginateUntilCurrentDoc (currentDocId, parentFolderId) {
			var deferred = $q.defer(),
				numPaginationCalls = 1;

			// This function simulates an iteration for making asynchronous calls.
			function next (done, position) {
				if (done || !markersByFolder[parentFolderId]) {
					// We can terminate the loop here since there are no more pages to get,
					// or we have found our document's position.
					deferred.resolve(position);
				} else if (numPaginationCalls > AppConstants.PAGINATION_LIMIT) {
					// Limit this to five calls (4000 items).
					// If we haven't figured out the position by then, we turn off the feature by returning a position of -1.
					deferred.resolve(position);
				} else {
					makePaginationCallInLoop(currentDocId, parentFolderId)
					.then(function success (response) {
						// Recursive call to continue to next iteration of loop
						next(response.done, response.position);
					}, function error (err) {
						deferred.reject(err);
					});
				}

				numPaginationCalls++;
			}

			// Initiate the loop
			next(false, -1);
			return deferred.promise;
		}

		/*
		 * Helper function: Makes a single pagination call within an asynchronous loop.
		 * @param {string} currentDocId
		 * @param {string} parentFolderId
		 * @return {promise} object containing a done boolean and a position integer
		 */
		function makePaginationCallInLoop (currentDocId, parentFolderId) {
			var deferred = $q.defer(),
				params = getFolderContentsParams;

			// We are guaranteed to have a marker here because of the first 'if' check in the #next function.
			params.marker = markersByFolder[parentFolderId];

			FileService.getFolderContents(parentFolderId, params)
			.then(function success (response) {
				var documents,
					numDocs,
					docs = docByFolder[parentFolderId],
					done = false,
					position = -1;

				if (response.Documents) {
					documents = response.Documents;
					numDocs = documents.length;
					for (var i = 0; i < numDocs; i++) {
						docs.push(documents[i]);
						if (documents[i].Id === currentDocId) {
							// We have found the current document, so we can terminate the loop.
							done = true;
							// The current document ID should be the last element in docs.
							position = docs.length - 1;
						}
					}

					// Store the new document ID list
					docByFolder[parentFolderId] = docs;
				}

				// Store the new marker for the parent folder.
				if (response.Marker) {
					markersByFolder[parentFolderId] = response.Marker;
				} else {
					markersByFolder[parentFolderId] = null;
				}

				deferred.resolve({
					done: done,
					position: position
				});
			}, function error (err) {
				deferred.reject(err);
			});

			return deferred.promise;
		}

		/*
		 * Validates the document by making a getDocument call and comparing its ParentFolderId to what is expected.
		 * @param {string} docId
		 * @param {string} expectedParentFolderId
		 * @return {boolean} true if docId's parent folder is the same as expected, false otherwise
		 */
		function validateDoc(docId, expectedParentFolderId) {
			var deferred = $q.defer(),
				docPromise = DocumentService.getDocument(docId);

			docPromise.then(function success (response) {
				if (response.data.Metadata.ParentFolderId === expectedParentFolderId) {
					// Store the response so we don't have to make a duplicate getDocument call when resolving the document route.
                    DocumentService.setDocData(docPromise);
                    deferred.resolve();
				} else {
					// The document has been moved to another folder or the recycle bin.
					deferred.reject();
				}
			}, function error (err) {
				if (err.status === 404) {
					// The document has been deleted permenantly.
					deferred.reject();
				} else {
					deferred.reject(err);
				}
			});

			return deferred.promise;
		}

		/*
		 * Continues to populate the doc ID list with the next page of documents.
		 * @param {string} folderId
		 * @return {promise} empty promise object
		 */
		function getNextPaginatedDocs(folderId) {
			var deferred = $q.defer(),
				params = getFolderContentsParams;

			params.marker = markersByFolder[folderId];

			FileService.getFolderContents(folderId, params)
			.then(function success (response) {
				var documents;

				if (response.Documents) {
					documents = response.Documents;
					docByFolder[folderId] = docByFolder[folderId].concat(documents);
					deferred.resolve();
				}

				if (response.Marker) {
					markersByFolder[folderId] = response.Marker;
				} else {
					markersByFolder[folderId] = null;
				}
			}, function error (err) {
				deferred.reject(err);
			});

			return deferred.promise;
		}

		/* This function returns document Id from document metadata */
		function doc2docId(doc) {
			return doc.Id;
		}

		function isImageFile(doc) {
			return ContentTypeFormatter.isImageFile(doc.VersionMetadata.ContentType);
		}

		return nextDocService;
	}

}());